import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@client-app/i18n';
import {
    ChartBar,
    CreditCard,
    Gear,
    ListChecks,
    SquaresFour,
    Stack,
    User,
    Users,
    X
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import BillingSettings from './components/BillingSettings';
import GeneralSettings from './components/GeneralSettings';
import IntegrationsSettings from './components/IntegrationsSettings';
import MembersSettings from './components/MembersSettings';
import ProfileSettings from './components/ProfileSettings';
import UsageSettings from './components/UsageSettings';
import ValueLedgerPage from '@/components/ValueLedger/ValueLedgerPage';
import WorkspaceSettings from './components/WorkspaceSettings';

export enum NavKey {
    general = 'general',
    profile = 'profile',
    brand = 'brand',
    usage = 'usage',
    valueLedger = 'value-ledger',
    billing = 'billing',
    integration = 'integration',
    workspaceSettings = 'workspace-settings',
    members = 'members',
}
// --- Helper Components ---

// --- Sub-Pages ---

// ProfileSettings imported from components/ProfileSettings

// BillingSettings imported from components/BillingSettings

// WorkspaceSettings imported from components/WorkspaceSettings

// --- Main Modal Component ---

const mapTab = (key: NavKey): string => {
    switch (key) {
        case NavKey.general: return 'general';
        case NavKey.profile: return 'profile';
        case NavKey.integration: return 'integrations';
        case NavKey.billing: return 'billing';
        case NavKey.valueLedger: return 'value-ledger';
        case NavKey.brand: return 'workspace-settings';
        case NavKey.workspaceSettings: return 'workspace-settings';
        case NavKey.members: return 'members';
        case NavKey.usage: return 'usage';
        default: return 'profile';
    }
};

const SettingsModal: React.FC = () => {
    const { t } = useTranslation();
    const { modal } = useAdminContext();
    const { integrations } = useAuth();
    const location = useLocation();
    const isOpen = modal.isModalOpen('profile');
    const modalConfig = modal.getModalConfig('profile');
    const activeTabFromConfig = (modalConfig?.activeTab as NavKey) || NavKey.profile;
    const initialTab = mapTab(activeTabFromConfig);
    const pathname = location.pathname;
    const isAuthRoute = pathname === '/auth'
        || pathname.startsWith('/auth/')
        || pathname === '/admin/auth'
        || pathname.startsWith('/admin/auth/');

    const [activeTab, setActiveTab] = useState(initialTab);

    const onClose = () => modal.closeModal('profile');
    const refreshIntegrations = integrations.refresh;
    const preloadIntegrations = useCallback(() => {
        void refreshIntegrations(false).catch(() => {});
    }, [refreshIntegrations]);

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    useEffect(() => {
        if (isOpen && initialTab === 'integrations') {
            preloadIntegrations();
        }
    }, [initialTab, isOpen, preloadIntegrations]);

    useEffect(() => {
        if (isAuthRoute && isOpen) {
            modal.closeModal('profile');
        }
    }, [isAuthRoute, isOpen, modal]);

    if (!isOpen || isAuthRoute) return null;

    const menuItems = [
        {
            category: t('website.settingsModal.account'),
            items: [
                {
                    id: 'profile',
                    label: t('sidebar.profile'),
                    icon: User,
                    description: t('website.settingsModal.profileDescription')
                },
                {
                    id: 'general',
                    label: t('website.settingsModal.general'),
                    icon: Gear,
                    description: t('website.settingsModal.generalDescription')
                },
            ]
        },
        {
            category: t('website.settingsModal.workspaceRecord'),
            items: [
                {
                    id: 'value-ledger',
                    label: t('website.settingsModal.valueLedger'),
                    icon: ListChecks,
                    description: t('website.settingsModal.valueLedgerDescription')
                },
            ]
        },
        {
            category: t('website.settingsModal.workspace'),
            items: [
                {
                    id: 'workspace-settings',
                    label: t('website.settingsModal.workspaceSettings'),
                    icon: Stack,
                    description: t('website.settingsModal.workspaceSettingsDescription')
                },
                {
                    id: 'integrations',
                    label: t('website.settingsModal.integrations'),
                    icon: SquaresFour,
                    description: t('website.settingsModal.integrationsDescription')
                },
                {
                    id: 'members',
                    label: t('website.settingsModal.users'),
                    icon: Users,
                    description: t('website.settingsModal.usersDescription')
                },
                {
                    id: 'billing',
                    label: t('website.settingsModal.billingAndPlans'),
                    icon: CreditCard,
                    description: t('website.settingsModal.billingDescription')
                },
                {
                    id: 'usage',
                    label: t('website.settingsModal.usage'),
                    icon: ChartBar,
                    description: t('website.settingsModal.usageDescription')
                },
            ]
        }
    ];

    const allTabs = menuItems.flatMap(g => g.items);
    const currentTabInfo = allTabs.find(t => t.id === activeTab);

    const renderContent = () => {
        switch (activeTab) {
            case 'general':
                return <GeneralSettings />;
            case 'profile':
                return <ProfileSettings />;
            case 'workspace-settings':
                return <WorkspaceSettings />;
            case 'integrations':
                return (
                    <IntegrationsSettings
                        workspaceIntegrations={integrations}
                        connectProvider={modalConfig?.data?.connectProvider}
                        connectRequestId={modalConfig?.data?.connectRequestId}
                    />
                );
            case 'members':
                return <MembersSettings />;
            case 'billing':
                return <BillingSettings onClose={onClose} />;
            case 'usage':
                return <UsageSettings />;
            case 'value-ledger':
                return <ValueLedgerPage />;
            default:
                return <div className="p-4 text-gray-500">{t('website.settingsModal.comingSoon')}</div>;
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-5 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="flex h-[min(88vh,980px)] w-full max-w-[1440px] overflow-hidden rounded-[24px] border border-white/70 bg-[#FCFCFC] shadow-[0_32px_80px_rgba(26,22,47,0.16)]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sidebar */}
                <div className="flex w-[288px] flex-col border-r border-[#E8EBF3] bg-[#F7F7F8] pt-5">
                    <div className="space-y-6 px-3 pb-6">
                        {menuItems.map((section) => (
                            <div key={section.category}>
                                <div className="mb-2 px-2 text-[11px] font-medium uppercase tracking-[0.08em] text-[#7D818A]">
                                    {section.category}
                                </div>
                                <div className="space-y-[2px]">
                                    {section.items.map((item) => {
                                        const isActive = activeTab === item.id;
                                        const Icon = item.icon;

                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => {
                                                    if (item.id === 'integrations') {
                                                        preloadIntegrations();
                                                    }
                                                    setActiveTab(item.id);
                                                }}
                                                onMouseEnter={item.id === 'integrations' ? preloadIntegrations : undefined}
                                                onFocus={item.id === 'integrations' ? preloadIntegrations : undefined}
                                                className={clsx(
                                                    "flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-[14px] font-normal transition-colors",
                                                    isActive
                                                        ? "bg-[#E6E7EA] text-[#44484F]"
                                                        : "text-[#44484F] hover:bg-[#ECEDEF]"
                                                )}
                                            >
                                                <Icon size={18} className="shrink-0" />
                                                <span className="flex-1 text-left truncate">{item.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden bg-[#F4F5F8]">
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute right-6 top-6 z-10 rounded-[10px] p-2 text-[#8A93B2] transition-colors hover:bg-white/80 hover:text-[#1A162F]"
                    >
                        <X size={20} weight="regular" />
                    </button>

                    <div className="flex-1 overflow-y-scroll [scrollbar-gutter:stable] custom-scrollbar">
                        <div className="max-w-[1120px] mx-auto px-10 py-7 pb-14 pr-20">
                            {activeTab !== 'integrations' && (
                                <div className="mb-8">
                                    <h2 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">{currentTabInfo?.label}</h2>
                                </div>
                            )}
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SettingsModal;
